import { getMonthName } from '@/lib/constants';
import type { OriginVacationWindow } from '@/lib/cross-calendar';
import { uiFb, getTemplateStr } from '@/lib/i18n';
import { LevelBadge } from './LevelBadge';

const weatherBarColors = ['', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-lime-400', 'bg-green-400'];

interface MonthDataItem {
  month: number;
  vw: OriginVacationWindow;
  dm: { weatherScore: number; seasonName: string; tempRange: string; crowdLevel: string; priceLevel: string } | null;
  holidayNames: string[];
}

interface MonthBreakdownListProps {
  monthData: MonthDataItem[];
  originName: string;
  originFlag: string;
  destinationName: string;
  lang: string;
}

export function MonthBreakdownList({ monthData, originName, originFlag, destinationName, lang }: MonthBreakdownListProps) {
  return (
    <div className="mb-8 border-t border-border-subtle pt-6">
      <h2 className="font-heading text-xl font-bold text-text-primary md:text-2xl">
        {getTemplateStr('seo.cross.month_heading', lang, { origin: originName, destination: destinationName })}
      </h2>
      <div className="mt-4 space-y-3">
        {monthData.map(md => (
          <div key={md.month} className="content-card p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary">
                {getMonthName(md.month - 1, lang)}
              </h3>
              {md.dm && (
                <div className="flex items-center gap-2">
                  <div className="flex h-3 w-16 overflow-hidden rounded-full bg-surface-root">
                    <div className={`h-full rounded-full ${weatherBarColors[md.dm.weatherScore]}`} style={{ width: `${md.dm.weatherScore * 20}%` }} />
                  </div>
                  <span className="text-caption text-text-secondary">{md.dm.weatherScore}/5</span>
                </div>
              )}
            </div>

            {/* Origin holidays */}
            <div className="mt-2">
              <span className="text-xs font-medium text-text-secondary">{originFlag} {uiFb('ui.your_holidays', 'Your holidays:', lang)}</span>
              <span className="text-xs text-text-primary">
                {md.holidayNames.length > 0 ? md.holidayNames.join(', ') : uiFb('ui.none', 'None', lang)}
              </span>
              {md.vw.totalDaysOff > 0 && (
                <span className="ml-2 rounded-full badge-success px-2 py-0.5 text-caption font-medium">
                  {getTemplateStr('seo.cross.days_off', lang, { count: String(md.vw.totalDaysOff) })}
                </span>
              )}
            </div>

            {/* Destination conditions */}
            {md.dm && (
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="rounded bg-surface-root px-2 py-0.5 text-text-secondary">{md.dm.seasonName}</span>
                <span className="rounded bg-surface-root px-2 py-0.5 text-text-secondary">{md.dm.tempRange}</span>
                <LevelBadge level={md.dm.crowdLevel} label={getTemplateStr('seo.cross.crowds_chip', lang, { level: md.dm.crowdLevel })} variant="chip" />
                <LevelBadge level={md.dm.priceLevel} label={getTemplateStr('seo.cross.prices_chip', lang, { level: md.dm.priceLevel })} variant="chip" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
